// @flow

import React from 'react';
import type { Node } from 'react';
import { Heading, Link, LazyloadPlaceholder } from '@riseart/common';
import { SectionContent } from '@riseart/layout';
import { HorizontalSlider } from '@riseart/slider';
import { Card, CardContent, CardMedia } from '@riseart/card';
import { PicturePlaceholder } from 'shared_components/common/artdirection/picture/Picture';

const LAZYLOAD_CONFIG = {
  once: true,
  height: 270,
  offset: 200,
  placeholder: <LazyloadPlaceholder height={270} />,
};

type Props = {
  title?: string | Node,
  subTitle?: string | Node,
  slides: Array<Object>,
  disableHeadingTags?: boolean,
};

/**
 * CmsModuleCommonSlider
 *
 * @param {Props} props
 * @returns {Node}
 */
export function CmsModuleCommonSlider({
  title,
  subTitle,
  slides,
  disableHeadingTags = false,
}: Props): Node {
  return (
    <SectionContent
      title={title}
      subTitle={subTitle}
      headingProps={{ tag: disableHeadingTags ? 'span' : 'h2', level: '2', align: 'center' }}
      headingSubTitleProps={{ tag: disableHeadingTags ? 'span' : 'h3', align: 'center' }}
    >
      <HorizontalSlider columnCount={4}>
        {slides && slides.length
          ? slides.map(({ title, link, image }, idx) => {
              const renderComponent = (children: Node) =>
                link && link.href ? (
                  <Link
                    to={link.href}
                    title={link.title || title}
                    external={link.external || false}
                    {...(link.target ? { target: link.target } : null)}
                  >
                    {children}
                  </Link>
                ) : (
                  children
                );
              return (
                <Card key={idx} showBorder={false}>
                  <CardMedia>
                    {renderComponent(
                      image ? (
                        <PicturePlaceholder
                          type="slider.gridLarge"
                          artDirectionKey="art.main"
                          image={image}
                          alt={title}
                          lazyload={LAZYLOAD_CONFIG}
                        />
                      ) : null,
                    )}
                  </CardMedia>
                  <CardContent>
                    <Heading level="4" tag={disableHeadingTags ? 'span' : 'h4'}>
                      {renderComponent(title)}
                    </Heading>
                  </CardContent>
                </Card>
              );
            })
          : null}
      </HorizontalSlider>
    </SectionContent>
  );
}
